/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { Button, Card, CardContent, CardFooter, CardHeader, Spinner } from '@repo/shared-client/components/ui'
import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { useAuth } from '@web/src/hooks/use-auth'
import { accountLoginRedirectHref } from '@web/src/lib/account/account-shortcut'
import { confirmMarketingUnsubscribe } from '@web/src/lib/email/confirm-marketing-unsubscribe'
import { useTranslation } from '@web/src/lib/intl/react'
import { useState } from 'react'
import { z } from 'zod'
import { marketingUnsubscribePageHead } from './marketing.seo'

export const Route = createFileRoute('/(public)/(main-layout)/unsubscribe/marketing')({
  component: MarketingUnsubscribePage,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  head: () => marketingUnsubscribePageHead(),
})

function MarketingUnsubscribePage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { token } = useSearch({ from: '/(public)/(main-layout)/unsubscribe/marketing' })
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>(token ? 'idle' : 'error')

  const handleConfirm = async () => {
    if (!token) {
      setStatus('error')
      return
    }

    setStatus('submitting')
    try {
      const result = await confirmMarketingUnsubscribe({ data: { token } })
      setStatus(result.success ? 'success' : 'error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
      <Card className="w-full max-w-lg">
        <CardHeader className="items-center gap-4">
          <LogoFull size="md" />
          <h1 className="text-center font-semibold text-2xl text-foreground">
            {status === 'success' ? t("You've unsubscribed") : t('Unsubscribe from marketing emails')}
          </h1>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-center text-muted-foreground">
          {status === 'success' ? (
            <p>
              {t(
                "You will no longer receive product updates and what's new emails. You can still manage other email preferences after you log in."
              )}
            </p>
          ) : null}
          {status === 'error' ? (
            <p>
              {t(
                'This unsubscribe link is invalid or has already been used incorrectly. You can log in to manage email settings.'
              )}
            </p>
          ) : null}
          {status === 'idle' || status === 'submitting' ? (
            <p>
              {t(
                "We'll stop sending product updates and what's new emails. Other account and activity emails are unchanged."
              )}
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          {status === 'idle' || status === 'submitting' ? (
            <Button disabled={status === 'submitting'} onClick={handleConfirm} size="lg">
              {status === 'submitting' ? <Spinner className="mr-2 size-4" /> : null}
              {t('Confirm unsubscribe')}
            </Button>
          ) : null}
          {isAuthenticated ? (
            <Button
              nativeButton={false}
              render={<Link to="/account/email" />}
              size="lg"
              variant={status === 'success' ? 'default' : 'outline'}
            >
              {t('Manage email settings')}
            </Button>
          ) : (
            <Button
              nativeButton={false}
              render={<Link href={accountLoginRedirectHref('/account/email')} />}
              size="lg"
              variant={status === 'success' ? 'default' : 'outline'}
            >
              {t('Log in')}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
